import React, { memo, useCallback, useMemo } from "react";
import { Form, Input, InputRef, message } from "antd";
import { FormInstance } from "antd/lib/form";
import { useState, useEffect, useRef, useContext } from "react";

const EditableContext = React.createContext<FormInstance<any> | null>(null);

interface EditableRowProps {
  index: number;
}

export const EditableRow = memo(({ index, ...props }: EditableRowProps) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
});

interface EditableCellProps {
  title: React.ReactNode;
  editable: boolean;
  children: React.ReactNode;
  dataIndex: string;
  record: any;
  handleSave: (record: any) => void;
}

export const EditableCell = memo(({ title, editable, children, dataIndex, record, handleSave, ...restProps }: EditableCellProps) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<InputRef>(null);
  const form = useContext(EditableContext)!;

  useEffect(() => {
    if (editing) {
      inputRef.current!.focus();
    }
  }, [editing]);

  const toggleEdit = useCallback(() => {
    setEditing(!editing);
    form.setFieldsValue({ [dataIndex]: record[dataIndex] });
  }, [dataIndex, editing, form, record]);

  const save = useCallback(async () => {
    try {
      const values = await form.validateFields();

      toggleEdit();
      await handleSave({ ...record, ...values });
    } catch (errInfo) {
      message.error({
        content: "ویرایش رکورد با خطا مواجه شد",
        key: "editRecord",
        duration: 5,
      });

      console.log("Save failed:", errInfo);
      // todo
    }
  }, [form, handleSave, record, toggleEdit]);

  const cancel = useCallback(() => {
    toggleEdit();
  }, [toggleEdit]);

  const childNode = useMemo(
    () =>
      editable ? (
        editing ? (
          <Form.Item style={{ margin: 0 }} name={dataIndex}>
            <Input ref={inputRef} onPressEnter={save} onBlur={cancel} />
          </Form.Item>
        ) : (
          <div className="editable-cell-value-wrap" onClick={toggleEdit}>
            {children}
          </div>
        )
      ) : (
        children
      ),
    [cancel, children, dataIndex, editable, editing, save, toggleEdit]
  );

  return <td {...restProps}>{childNode}</td>;
});
